import { MouseEvent, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useField } from 'react-final-form';
import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import { DECISION_INFO } from 'CorePackage/MainModule/routes/routes';
import {
  KNO_ORGANIZATION_BLOCK_ID,
  BASIS_DOCUMENT_DATE_TIME_BLOCK_ID,
  BASIS_DOCUMENT_NUMBER_BLOCK_ID,
  KIND_BLOCK_ID,
  KIND_CONTROL_BLOCK_ID,
} from 'DecisionPackage/MainModule/constants/decisionFieldsElementsIds';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { getDictionariesKnoOrganizationsSelectOptions } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { Row } from 'UIPackage/FormModule/components/Row';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import {
  getDecisionDataAccessToken,
  getDecisionDataDateReasonChangeKno,
  getDecisionDataDecisionId,
  getDecisionDataOldKnoOrganization,
  getDecisionDataSignatureInfo,
  getDecisionDataSigned,
  getDecisionDecisionKindsSelectOptions,
  getDecisionFormRef,
  getDecisionIsNew,
  getDecisionMassCancel,
  getDecisionMassDelete,
  getDecisionSupervisionTypesSelectOptions,
  getIsDecisionReasonValidate,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IDictionaryDecisionKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryDecisionKindSelectOption';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { IKnmSignatureInfo } from 'APIPackage/KnmModule/interfaces/IKnmSignatureInfo';
import { getPublicPortalURL } from 'CorePackage/MainModule/reducers/coreReducer';
import { IDecisionFormReason } from 'DecisionPackage/MainModule/interfaces/IDecisionFormReason';

import styles from './DecisionInfo.module.scss';
import { getUserRoleIsSupervisor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { Tooltip } from '../../../UIPackage/MainModule/components/Tooltip';
import { TitleSectionElements } from './TitleSectionElements';
import { DecisionReason } from './DecisionReason';
import { MassDeleteInfo } from '../../../KnmPackage/MainModule/components/MassDeleteInfo';
import { IKnmMassDelete } from '../../../APIPackage/KnmModule/interfaces/IKnmMassDelete';
import { MassCancelInfo } from '../../../KnmPackage/MainModule/components/MassCancelInfo';
import { IKnmMassCancel } from '../../../APIPackage/KnmModule/interfaces/IKnmMassCancel';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { Hint } from '../../../CorePackage/MainModule/components/Hint';
import { useKnoNameChangeText } from '../../../KnmPackage/MainModule/hooks/useKnmNameChangeHintText';

interface IDecisionInfoProps {
  className?: string;
  created: string | undefined;
  isWatchOnly: boolean;
  kindControlFormValue?: IDictionarySupervisionTypeSelectOption;
  reasons?: IDecisionFormReason[] | undefined;
  statusCode?: IKnmStatus;
}

interface IDecisionInfoStoreProps {
  accessToken: string | null | undefined;
  dateReasonChangeKno: string | null | undefined;
  decisionId: string | undefined;
  decisionKindsSelectOptions: IDictionaryDecisionKindSelectOption[] | undefined;
  formRef: IDecisionFormRef | undefined;
  isDecisionReasonValidate: boolean;
  isNewDecision: boolean;
  isSupervisor: boolean;
  knoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  massCancelData: IKnmMassCancel | null | undefined;
  massDeleteData: IKnmMassDelete | null | undefined;
  oldKnoOrganization: string | null | undefined;
  publicPortalURL: string;
  signatureInfo: IKnmSignatureInfo[] | null | undefined;
  signed: boolean | null | undefined;
  supervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
}

const defaultReason: IDecisionFormReason = {
  reason: undefined,
  type: undefined,
  text: undefined,
  date: undefined,
  riskIndicators: undefined,
  requirementDetails: undefined,
  dateRequirement: undefined,
  assignmentNumber: undefined,
  assignmentDate: undefined,
  numGuid: undefined,
  dateCreate: undefined,
  dateBegin: undefined,
  dateEnd: undefined,
  order: undefined,
  ervkUuid: undefined,
  links: undefined,
  listId: uuidv4(),
  fullName: undefined,
  indicatorRiskAttachment: undefined,
  indicatorRiskAuto: undefined,
  lastConductedKnmDate: undefined,
  prescriptionDate: undefined,
  prescriptionNumber: undefined,
  programAttachment: undefined,
  programNumber: undefined,
  prosecutorAttachment: undefined,
};

const DecisionInfoWithoutStore = ({
  className = '',
  created,
  isWatchOnly,
  kindControlFormValue,
  reasons,
  statusCode,
  accessToken,
  dateReasonChangeKno,
  decisionId,
  decisionKindsSelectOptions,
  formRef,
  isDecisionReasonValidate,
  isNewDecision,
  isSupervisor,
  knoOrganizationsSelectOptions,
  massCancelData,
  massDeleteData,
  oldKnoOrganization,
  publicPortalURL,
  signatureInfo,
  signed,
  supervisionTypesSelectOptions,
}: IDecisionInfoProps & IDecisionInfoStoreProps) => {
  const isDisableInfo = !isSupervisor || !isNewDecision;
  const qrRef = useRef<HTMLDivElement>(null);

  const handleKnoOrganizationChange = useCallback(() => {
    formRef?.current.change('kindControl');
    formRef?.current.change('kind');
    formRef?.current.change('organization');
    formRef?.current.change('objects');
    formRef?.current.change('reasons', [defaultReason]);
  }, [formRef]);

  useEffect(() => {
    if (!reasons || reasons.length === 0) {
      formRef?.current.change('reasons', [defaultReason]);
    }
  }, [isDecisionReasonValidate]);

  const handleKindControlChange = useCallback(
    (value: IDictionarySupervisionTypeSelectOption) => {
      // TODO добавить сброс полей после выбора вида контроля
      formRef?.current.change(
        'kindControl',
        !value
          ? value
          : {
              ...value,
              label: value.selectedLabel || value.label,
            },
      );
      formRef?.current.change('kind');
      formRef?.current.change('organization');
      formRef?.current.change('objects');
      if (isDecisionReasonValidate) formRef?.current.change('reasons', [defaultReason]);
    },
    [formRef, isDecisionReasonValidate],
  );

  const handleKindChange = useCallback(() => {
    formRef?.current.change('organization');
    formRef?.current.change('objects');
    if (isDecisionReasonValidate) formRef?.current.change('reasons', [defaultReason]);
  }, [formRef, isDecisionReasonValidate]);

  const handleDownloadButton = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const canvas = qrRef.current?.lastChild as HTMLCanvasElement;
      const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');

      saveFile(pngUrl, `${decisionId ?? 'qr'}.png`);
    },
    [decisionId],
  );

  const knoOrganizationLabel: string | undefined = useField('knoOrganization', {
    subscription: { value: true },
  }).input.value?.label;
  const knoNameChangeText = useKnoNameChangeText(knoOrganizationLabel, oldKnoOrganization, dateReasonChangeKno);

  return (
    <div className={className ? `${styles.Wrapper} ${className}` : styles.Wrapper} id={DECISION_INFO}>
      <div className={styles.TitleContainer}>
        <h2 className="shared-title__with-margin">Общая информация</h2>
        {accessToken && (
          <TitleSectionElements
            isSigned={signed}
            signatureInfo={signatureInfo}
            publicPortalURL={publicPortalURL}
            qrRef={qrRef}
            accessToken={accessToken}
            handleDownloadButton={handleDownloadButton}
          />
        )}
      </div>

      <div className={styles.KnoOrganizationFieldBlock} id={KNO_ORGANIZATION_BLOCK_ID}>
        <div className="shared-group-margin">
          <label className="shared-group-label" htmlFor="knoOrganizationPm">
            {getTitleFieldWithRedStar({
              label: 'Контрольный (надзорный) орган',
              field: `knoOrganization`,
              isDecision: true,
            })}
          </label>
        </div>
        <SelectInputField
          id="knoOrganization"
          name="knoOrganization"
          options={knoOrganizationsSelectOptions}
          isClearable
          isSearchable
          disabled={Boolean(statusCode) || isWatchOnly || !isNewDecision || isDisableInfo}
          size="medium"
          onChange={handleKnoOrganizationChange}
        />
        {knoNameChangeText && <Hint margin="top" text={knoNameChangeText} />}
      </div>

      <div className={styles.KindControlFieldBlock} id={KIND_CONTROL_BLOCK_ID}>
        <div className="shared-group-margin">
          <label className="shared-group-label" htmlFor="supervisionTypePm">
            {getTitleFieldWithRedStar({
              label: 'Вид контроля (надзора) и его номер',
              field: `kindControl`,
              isDecision: true,
            })}
          </label>
          <Tooltip
            placement="right"
            type="info"
            content={
              <div className="shared-tooltip-info-content">
                Поле зависит от значения в следующем поле:
                <ul>
                  <p>Контрольный (надзорный) орган</p>
                </ul>
              </div>
            }
          >
            <span className="shared-tooltip-info-icon" />
          </Tooltip>
        </div>
        <SelectInputField
          id="kindControl"
          name="kindControl"
          options={supervisionTypesSelectOptions}
          isSearchable
          disabled={isWatchOnly || !isNewDecision || isDisableInfo}
          size="medium"
          onChange={handleKindControlChange}
        />
      </div>
      {kindControlFormValue !== null && (
        <>
          <div className={styles.KindFieldBlock} id={KIND_BLOCK_ID}>
            <div className="shared-group-margin">
              <label className="shared-group-label" htmlFor="pmType">
                {getTitleFieldWithRedStar({
                  label: 'Вид контрольного (надзорного) мероприятия, специального режима',
                  field: `kind`,
                  isDecision: true,
                })}
              </label>
              <Tooltip
                placement="right"
                type="info"
                content={
                  <div className="shared-tooltip-info-content">
                    Поле зависит от значения в следующем поле:
                    <ul>
                      <p>Контрольный (надзорный) орган</p>
                      <p>Вид контроля (надзора)</p>
                    </ul>
                  </div>
                }
              >
                <span className="shared-tooltip-info-icon" />
              </Tooltip>
            </div>
            <SelectInputField
              id="kind"
              name="kind"
              options={decisionKindsSelectOptions}
              disabled={Boolean(statusCode) || isWatchOnly || isDisableInfo}
              size="medium"
              onChange={handleKindChange}
            />
          </div>

          {!isDecisionReasonValidate && (
            <>
              <div className={cn(['shared-group-label', styles.BasisDocumentTitle])}>
                Дата и номер документа, на основании которого проводится мероприятие
              </div>

              <div
                className={cn(['shared-field-row', styles.BasisDocumentStartDateBlock])}
                id={BASIS_DOCUMENT_DATE_TIME_BLOCK_ID}
              >
                <div className="shared-field-row-block_width">
                  <label className="shared-field-label_row" htmlFor="startDate">
                    Дата документа
                  </label>
                </div>
                <DatePickerField
                  className="shared-row-field"
                  id="basisDocumentDateTime"
                  name="basisDocumentDateTime"
                  disabled={isWatchOnly || isDisableInfo}
                  timeFormat
                />
              </div>

              <div
                className={cn(['shared-field-row', styles.BasisDocumentNumberBlock])}
                id={BASIS_DOCUMENT_NUMBER_BLOCK_ID}
              >
                <div className="shared-field-row-block_width">
                  <label className="shared-field-label_row" htmlFor="stopDate">
                    Номер документа
                  </label>
                </div>
                <Row align="center">
                  <TextInputField name="basisDocumentNumber" disabled={isDisableInfo} />
                </Row>
              </div>
            </>
          )}

          {isDecisionReasonValidate && <DecisionReason created={created} />}
        </>
      )}
      <MassCancelInfo massCancel={massCancelData} />
      <MassDeleteInfo massDelete={massDeleteData} />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IDecisionInfoStoreProps => ({
  accessToken: getDecisionDataAccessToken(state),
  dateReasonChangeKno: getDecisionDataDateReasonChangeKno(state),
  decisionId: getDecisionDataDecisionId(state),
  decisionKindsSelectOptions: getDecisionDecisionKindsSelectOptions(state),
  formRef: getDecisionFormRef(state),
  isDecisionReasonValidate: getIsDecisionReasonValidate(state),
  isNewDecision: getDecisionIsNew(state) || false,
  isSupervisor: getUserRoleIsSupervisor(state),
  knoOrganizationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
  massCancelData: getDecisionMassCancel(state),
  massDeleteData: getDecisionMassDelete(state),
  oldKnoOrganization: getDecisionDataOldKnoOrganization(state),
  publicPortalURL: getPublicPortalURL(state),
  signatureInfo: getDecisionDataSignatureInfo(state),
  signed: getDecisionDataSigned(state),
  supervisionTypesSelectOptions: getDecisionSupervisionTypesSelectOptions(state),
});

export const DecisionInfo = connect(mapStateToProps)(DecisionInfoWithoutStore);
